import classNames from "classnames";
import { gql } from "@apollo/client";
import {
  LinkedinSalesNavigatorFilters,
  InternalCuratedSearchFilters,
  AutopilotSessionFiltersDisplay_AutopilotSessionFragment,
} from "../../../../__generated__/graphql";
import FilterItem, {
  AutopilotDisplayableFilterItem,
  AutopilotFilterType,
} from "./FilterItem";

export const AutopilotSessionFiltersDisplay_AutopilotSession = gql`
  fragment AutopilotSessionFiltersDisplay_AutopilotSession on AutopilotSession {
    source
    curatedSearch {
      linkedinSalesNavigatorCuratedSearch {
        filters {
          keywords
          jobTitles {
            label
          }
          industries {
            id
            label
          }
          geographies {
            id
            label
          }
          companyHQLocations {
            id
            label
          }
          companyHeadCounts {
            id
            label
          }
          companyNames {
            label
          }
          excludedCompanyNames {
            label
          }
        }
      }
      internalCuratedSearch {
        filters {
          jobTitles
          companyKeywords
          profileSummaryKeywords
          industries {
            label
          }
          geographies {
            id
            label
          }
          companyHQLocations {
            id
            label
          }
          companyHQRegions {
            label
          }
          companyHQCities {
            label
          }
          contactRegions {
            label
          }
          contactCities {
            label
          }
          companyHeadCountRange {
            lowerBound
            upperBound
          }
          companyNames {
            label
          }
        }
      }
    }
  }
`;

interface IAutopilotSessionFiltersDisplay {
  autopilotSession?: AutopilotSessionFiltersDisplay_AutopilotSessionFragment | null;
}

const AutopilotSessionFiltersDisplay = ({
  autopilotSession,
}: IAutopilotSessionFiltersDisplay) => {
  const lsnFilters =
    autopilotSession?.curatedSearch?.linkedinSalesNavigatorCuratedSearch
      ?.filters;
  const internalFilters =
    autopilotSession?.curatedSearch?.internalCuratedSearch?.filters;
  const activeFilters = lsnFilters || internalFilters;

  if (!activeFilters) {
    return null;
  }

  // Build filter items based on what type of filters we have
  const filterItems: AutopilotDisplayableFilterItem[] = [];

  // Define the order for LSN filters based on the curated search filter cards
  const lsnFilterOrder = [
    {
      key: "keywords",
      label: "Keywords",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        Boolean(filters.keywords),
      getData: (filters: LinkedinSalesNavigatorFilters) => filters.keywords,
      type: AutopilotFilterType.REGULAR_TEXT,
    },
    {
      key: "jobTitles",
      label: "Job titles",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.jobTitles && filters.jobTitles.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) => filters.jobTitles,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "companyNames",
      label: "Company names",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.companyNames && filters.companyNames.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) => filters.companyNames,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "excludedCompanyNames",
      label: "Excluded company names",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.excludedCompanyNames &&
        filters.excludedCompanyNames.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) =>
        filters.excludedCompanyNames,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "companyHeadCounts",
      label: "Company head count",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.companyHeadCounts && filters.companyHeadCounts.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) =>
        filters.companyHeadCounts,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "companyHQLocations",
      label: "Company HQ",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.companyHQLocations && filters.companyHQLocations.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) =>
        filters.companyHQLocations,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "geographies",
      label: "Geographies",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.geographies && filters.geographies.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) => filters.geographies,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "industries",
      label: "Industries",
      check: (filters: LinkedinSalesNavigatorFilters) =>
        !!filters.industries && filters.industries.length > 0,
      getData: (filters: LinkedinSalesNavigatorFilters) => filters.industries,
      type: AutopilotFilterType.MULTI_ITEM,
    },
  ];

  // Define the order for internal filters based on similar ordering
  const internalFilterOrder = [
    {
      key: "jobTitles",
      label: "Job titles",
      check: (filters: InternalCuratedSearchFilters) => !!filters.jobTitles,
      getData: (filters: InternalCuratedSearchFilters) => filters.jobTitles,
      type: AutopilotFilterType.EXPRESSION,
    },
    {
      key: "companyKeywords",
      label: "Company keywords",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.companyKeywords,
      getData: (filters: InternalCuratedSearchFilters) =>
        filters.companyKeywords,
      type: AutopilotFilterType.EXPRESSION,
    },
    {
      key: "profileSummaryKeywords",
      label: "Profile summary keywords",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.profileSummaryKeywords,
      getData: (filters: InternalCuratedSearchFilters) =>
        filters.profileSummaryKeywords,
      type: AutopilotFilterType.EXPRESSION,
    },
    {
      key: "companyNames",
      label: "Company names",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.companyNames && filters.companyNames.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.companyNames,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "companyHeadCountRange",
      label: "Company head count range",
      check: (filters: InternalCuratedSearchFilters) => {
        if (!filters.companyHeadCountRange) return false;
        const { lowerBound, upperBound } = filters.companyHeadCountRange;
        return lowerBound !== null || upperBound !== null;
      },
      getData: (filters: InternalCuratedSearchFilters) =>
        filters.companyHeadCountRange,
      type: AutopilotFilterType.RANGE,
    },
    {
      key: "companyHQLocations",
      label: "Company HQ locations",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.companyHQLocations && filters.companyHQLocations.length > 0,
      getData: (filters: InternalCuratedSearchFilters) =>
        filters.companyHQLocations,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "companyHQRegions",
      label: "Company HQ regions",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.companyHQRegions && filters.companyHQRegions.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.companyHQRegions,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "companyHQCities",
      label: "Company HQ cities",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.companyHQCities && filters.companyHQCities.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.companyHQCities,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "contactRegions",
      label: "Contact regions",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.contactRegions && filters.contactRegions.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.contactRegions,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "contactCities",
      label: "Contact cities",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.contactCities && filters.contactCities.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.contactCities,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "geographies",
      label: "Geographies",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.geographies && filters.geographies.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.geographies,
      type: AutopilotFilterType.MULTI_ITEM,
    },
    {
      key: "industries",
      label: "Industries",
      check: (filters: InternalCuratedSearchFilters) =>
        !!filters.industries && filters.industries.length > 0,
      getData: (filters: InternalCuratedSearchFilters) => filters.industries,
      type: AutopilotFilterType.MULTI_ITEM,
    },
  ];

  // Apply the appropriate filter order based on filter type
  if (lsnFilters) {
    lsnFilterOrder.forEach(({ check, label, getData, type }) => {
      if (check(lsnFilters)) {
        filterItems.push({
          label,
          data: getData(lsnFilters),
          type,
        });
      }
    });
  } else if (internalFilters) {
    internalFilterOrder.forEach(({ check, label, getData, type }) => {
      if (check(internalFilters)) {
        filterItems.push({
          label,
          data: getData(internalFilters),
          type,
        });
      }
    });
  }

  return (
    <div
      className={classNames(
        "mb-4",
        "border",
        "border-gray-200",
        "rounded-lg",
        "bg-white",
        "shadow-sm",
        "transition-all",
        "duration-300",
        "ease-in-out",
        "overflow-hidden",
      )}
    >
      <div className={classNames("p-5", "text-sm", "rounded-lg")}>
        {filterItems.length > 0 ? (
          <div
            className={classNames(
              "grid",
              "grid-cols-1",
              "md:grid-cols-2",
              "gap-3",
            )}
          >
            {filterItems.map((item) => (
              <FilterItem key={item.label} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-gray-500">
            No filters were applied for this search.
          </p>
        )}
      </div>
    </div>
  );
};

export default AutopilotSessionFiltersDisplay;
